import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsersWithPermissions,
  savePermissions,
  resetPermissionStatus,
} from "../../../slice/permission/permissionSlice";

const ALL_MENUS = [
  { key: "dashboard",   label: "Dashboard",    icon: "solar:widget-5-bold-duotone" },
  { key: "category",    label: "Category",     icon: "solar:shop-bold-duotone" },
  { key: "subCategory", label: "Sub Category", icon: "solar:box-bold-duotone" },
  { key: "products",    label: "Products",     icon: "solar:t-shirt-bold-duotone" },
  { key: "orders",      label: "Orders",       icon: "solar:bag-check-bold-duotone" },
  { key: "color",       label: "Color",        icon: "solar:volleyball-bold-duotone" },
  { key: "size",        label: "Size",         icon: "solar:clipboard-list-bold-duotone" },
  { key: "label",       label: "Label",        icon: "solar:tuning-2-bold-duotone" },
  { key: "banner",      label: "Banner",       icon: "solar:bookmark-square-bold-duotone" },
  { key: "coupon",      label: "Coupon",       icon: "solar:bookmark-square-bold-duotone" },
  { key: "settings",    label: "Settings",     icon: "solar:card-send-bold-duotone" },
];

export default function ManagePermissions() {
  const dispatch = useDispatch();
  const { usersWithPermissions, loading, saveSuccess, error } = useSelector(
    (state) => state.permission
  );

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [localPermissions, setLocalPermissions] = useState({});
  const [savedAlert, setSavedAlert] = useState(false);

  useEffect(() => {
    dispatch(fetchUsersWithPermissions());
  }, [dispatch]);

  /* Auto-select first user */
  useEffect(() => {
    if (usersWithPermissions.length > 0 && !selectedUserId) {
      const first = usersWithPermissions[0];
      setSelectedUserId(first._id);
      setLocalPermissions(first.permissions);
    }
  }, [usersWithPermissions]);

  /* Handle save success */
  useEffect(() => {
    if (saveSuccess) {
      setSavedAlert(true);
      dispatch(fetchUsersWithPermissions());
      dispatch(resetPermissionStatus());
      setTimeout(() => setSavedAlert(false), 3000);
    }
  }, [saveSuccess]);

  const handleSelectUser = (user) => {
    setSelectedUserId(user._id);
    setLocalPermissions({ ...user.permissions });
  };

  const handleToggle = (key) => {
    setLocalPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectAll = () => {
    const allTrue = {};
    ALL_MENUS.forEach((m) => (allTrue[m.key] = true));
    setLocalPermissions(allTrue);
  };

  const handleClearAll = () => {
    const allFalse = {};
    ALL_MENUS.forEach((m) => (allFalse[m.key] = false));
    setLocalPermissions(allFalse);
  };

  const handleSave = () => {
    if (!selectedUserId) return;
    dispatch(savePermissions({ userId: selectedUserId, permissions: localPermissions }));
  };

  const selectedUser = usersWithPermissions.find((u) => u._id === selectedUserId);

  return (
    <div style={{ padding: "24px" }}>
      {/* PAGE HEADER */}
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>
          Manage Permissions
        </h2>
        <p style={{ color: "#6b7280", marginTop: "4px", fontSize: "14px" }}>
          Assign menu access permissions to admin users
        </p>
      </div>

      {savedAlert && (
        <div style={{
          background: "#d1fae5", color: "#065f46", padding: "10px 16px",
          borderRadius: "8px", marginBottom: "16px", fontSize: "14px",
          fontWeight: 500, border: "1px solid #6ee7b7"
        }}>
          ✓ Permissions saved successfully!
        </div>
      )}

      {error && (
        <div style={{
          background: "#fee2e2", color: "#991b1b", padding: "10px 16px",
          borderRadius: "8px", marginBottom: "16px", fontSize: "14px",
          border: "1px solid #fca5a5"
        }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

        {/* LEFT: USER LIST */}
        <div style={{
          width: "240px", flexShrink: 0,
          background: "#fff", borderRadius: "12px",
          border: "1px solid #e5e7eb", overflow: "hidden"
        }}>
          <div style={{
            padding: "14px 16px", background: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            fontSize: "13px", fontWeight: 600, color: "#374151"
          }}>
            Admin Users
          </div>

          {loading && usersWithPermissions.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af" }}>
              Loading...
            </div>
          ) : usersWithPermissions.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
              No admin users found
            </div>
          ) : (
            usersWithPermissions.map((user) => (
              <div
                key={user._id}
                onClick={() => handleSelectUser(user)}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f3f4f6",
                  background: selectedUserId === user._id ? "#fff7ed" : "#fff",
                  borderLeft: selectedUserId === user._id ? "3px solid #f97316" : "3px solid transparent",
                  transition: "all 0.15s",
                }}
              >
                <div style={{
                  fontWeight: selectedUserId === user._id ? 600 : 400,
                  fontSize: "14px",
                  color: selectedUserId === user._id ? "#f97316" : "#111827"
                }}>
                  {user.username}
                </div>
                <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                  Admin
                </div>
              </div>
            ))
          )}
        </div>

        {/* RIGHT: PERMISSIONS TABLE */}
        <div style={{ flex: 1, background: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>

          {/* Table Header */}
          <div style={{
            padding: "14px 20px", background: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <div>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                Menu Permissions
              </span>
              {selectedUser && (
                <span style={{
                  marginLeft: "10px", fontSize: "12px",
                  background: "#fff7ed", color: "#f97316",
                  padding: "2px 8px", borderRadius: "20px",
                  border: "1px solid #fed7aa"
                }}>
                  {selectedUser.username}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleSelectAll}
                style={{
                  fontSize: "12px", padding: "5px 12px",
                  borderRadius: "6px", border: "1px solid #d1d5db",
                  background: "#fff", cursor: "pointer", color: "#374151"
                }}
              >
                Select All
              </button>
              <button
                onClick={handleClearAll}
                style={{
                  fontSize: "12px", padding: "5px 12px",
                  borderRadius: "6px", border: "1px solid #d1d5db",
                  background: "#fff", cursor: "pointer", color: "#374151"
                }}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Permission Rows */}
          {!selectedUserId ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
              Select an admin user to manage permissions
            </div>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th style={{ padding: "10px 20px", textAlign: "left", fontSize: "12px", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>
                      Menu
                    </th>
                    <th style={{ padding: "10px 20px", textAlign: "center", fontSize: "12px", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>
                      Access
                    </th>
                    <th style={{ padding: "10px 20px", textAlign: "center", fontSize: "12px", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_MENUS.map((menu, idx) => (
                    <tr
                      key={menu.key}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        background: idx % 2 === 0 ? "#fff" : "#fafafa",
                        transition: "background 0.15s"
                      }}
                    >
                      {/* Menu Name */}
                      <td style={{ padding: "14px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{
                            width: "32px", height: "32px", borderRadius: "8px",
                            background: localPermissions[menu.key] ? "#fff7ed" : "#f3f4f6",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "background 0.2s"
                          }}>
                            <iconify-icon
                              icon={menu.icon}
                              style={{
                                fontSize: "18px",
                                color: localPermissions[menu.key] ? "#f97316" : "#9ca3af"
                              }}
                            />
                          </span>
                          <span style={{ fontSize: "14px", fontWeight: 500, color: "#111827" }}>
                            {menu.label}
                          </span>
                        </div>
                      </td>

                      {/* Toggle Switch */}
                      <td style={{ padding: "14px 20px", textAlign: "center" }}>
                        <label style={{ position: "relative", display: "inline-block", width: "44px", height: "24px", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={!!localPermissions[menu.key]}
                            onChange={() => handleToggle(menu.key)}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            position: "absolute", inset: 0,
                            background: localPermissions[menu.key] ? "#f97316" : "#d1d5db",
                            borderRadius: "24px",
                            transition: "background 0.2s"
                          }} />
                          <span style={{
                            position: "absolute",
                            top: "3px",
                            left: localPermissions[menu.key] ? "23px" : "3px",
                            width: "18px", height: "18px",
                            background: "#fff",
                            borderRadius: "50%",
                            transition: "left 0.2s",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          }} />
                        </label>
                      </td>

                      {/* Status Badge */}
                      <td style={{ padding: "14px 20px", textAlign: "center" }}>
                        <span style={{
                          fontSize: "12px", fontWeight: 500,
                          padding: "3px 10px", borderRadius: "20px",
                          background: localPermissions[menu.key] ? "#d1fae5" : "#fee2e2",
                          color: localPermissions[menu.key] ? "#065f46" : "#991b1b",
                        }}>
                          {localPermissions[menu.key] ? "Allowed" : "Denied"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* SAVE BUTTON */}
              <div style={{
                padding: "16px 20px",
                borderTop: "1px solid #e5e7eb",
                display: "flex", justifyContent: "flex-end",
                background: "#f9fafb"
              }}>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    padding: "9px 24px",
                    borderRadius: "8px",
                    border: "none",
                    background: loading ? "#fed7aa" : "#f97316",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Saving..." : "Save Permissions"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}